<template>
	<div>
		<Switcher :properties="currentWrapper" @close="closeModal" @submit="storeSelected">
			<!-- Modal trigger that lives inside the form -->
			<template #trigger>
				<div v-if="isMobile" :class="{'has-error' : hasError}">
					<!-- Capture the event on the parent first -->
					<div @click.capture.stop="openModal">
						<!-- Prevent triggering mobile keyboards -->
						<input
							class="mobile-trigger input"
							type="text"
							placeholder="Date"
							:value="displayDate"
							@click.self.prevent=""
						/>
					</div>
				</div>
			</template>
			<!-- Modal content -->
			<datepicker
				ref="baseDatePicker"
				language="en"
				placeholder="Date"
				:monday-first="true"
				:class="[
					'booking-times__date type--s',
					{'align-right': alignRight},
					{'is-mobile': isMobile}
				]"
				:format="'Do MMM'"
				:disabled="disabled"
				:disabled-dates="until"
				:open-date="openDate"
				:value="value"
				:inline="isMobile"
				@opened="setOpenAt"
				@input="handleSelectedDate"
			/>
		</Switcher>
	</div>
</template>

<script>
/* eslint-disable vue/no-unused-components */

// import Datepicker from "vuejs-datepicker"
import Datepicker from "@hokify/vuejs-datepicker"
import Switcher from "../FlytSwitcher"
import FlytModal from "../FlytModal"
import Simple from "../FlytSimple"
import { mapGetters } from "vuex"
import addOrdinalSuffix from "../../utils/addOrdinalSuffix"

export default {
	data() {
		return {
			// Open calendar at today
			openDate: new Date(),
			// Temporary selected date, not yet saved
			selectedDate: "",
			// Mobile trigger display date
			displayDate: "",
			// Switcher component properties
			componentProps: {
				simple: {
					component: Simple,
					data: {},
				},
				modal: {
					component: FlytModal,
					data: {
						selector: "#m-input",
						type: "drawerUp",
						title: "Select your Date",
						cancel: "Cancel",
						visible: false,
					},
				},
			},
		}
	},

	name: "FlytDateSelector",

	components: { Datepicker, Switcher, FlytModal, Simple },

	props: {
		alignRight: {
			type: Boolean,
			default: false
		},
		value: [String, Date],
		disabled: {
			type: Boolean,
			default: false
		},
		disabledUntil: {
			type: Date,
			default: () => new Date()
		},
		hasError: {
			type: Boolean,
			default: false
		},
	},

	computed: {
		...mapGetters("browser", ["isMobile"]),
		...mapGetters("bookingForm", ["formattedDate"]),
		currentWrapper() {
			let component = this.isMobile ? "modal" : "simple"
			return this.componentProps[component]
		},
		until() {
			return {
				to: this.disabledUntil
			}
		}
	},

	watch: {
		/**
		 * Watch the selected value, and populate the mobile trigger accordingly
		 */
		value(value) {
			if (value) this.displayDate = this.formatDateString(value)
		},
	},

	methods: {
		openModal(evt) {
			evt.srcElement.blur()
			this.componentProps.modal.data.visible = true
		},

		closeModal() {
			this.componentProps.modal.data.visible = false
		},

		/**
		 * What to do when a date is clicked
		 */
		handleSelectedDate(newValue) {
			this.storeDate(newValue)
			if (this.isMobile)
				this.closeModal()
		},

		/**
		 * Mobile submit stores the (temporary) selected date
		 */
		storeSelected() {
			this.$emit("input", this.selectedDate)
			this.closeModal()
		},

		/**
		 * Store date in non-mobile datepicker
		 */
		storeDate(newValue) {
			this.$emit("input", newValue)
		},

		/**
		 * Return a formatted date in the format XXth XXX
		 */
		formatDateString(dateStr) {
			let date = dateStr.toLocaleString("default", { day: "numeric" })
			let month = dateStr.toLocaleString("default", { month: "short" })
			return `${addOrdinalSuffix(date)} ${month}`
		},

		/**
		 * Reset all calendar data.
		 */
		reset() {
			this.displayDate = "";
			this.storeDate("");
			this.$refs.baseDatePicker?.clearDate();
		},

		/**
		 * Open the caledar at today's date, or, if it's
		 * the last day of the month, tomorrow's date.
		 */
		setOpenAt() {
			if (this.isTheLastDayOfTheMonth()) {
				this.openDate = new Date(this.openDate.setDate(this.openDate.getDate() + 1))
			}
		},

		/**
		 * Check whether today is the last day of the month
		 */
		isTheLastDayOfTheMonth() {
			let today = new Date(),
				month = today.getMonth()
			today.setDate(today.getDate() + 1)
			return today.getMonth() !== month
		},
	},
}
</script>

<style lang="scss">
.vdp-datepicker {
	// Native HTML input
	input {
		border-radius: 0.25rem 0 0 0.25rem;
		cursor: pointer;
		padding-left: 0.5rem;
		padding-right: 0.5rem;
	}
}

.mobile-trigger {
	border-radius: 0.25rem 0 0 0.25rem;
	cursor: pointer;
	padding-left: 0.5rem;
	padding-right: 0.5rem;
}

// Actual calendar components
.vdp-datepicker__calendar {
	background: #fff;
	border: none;
	border-radius: 0.25rem;
	box-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.15);
	font-family: "Overpass", sans-serif;
	overflow: hidden;
	position: absolute;
	top: -12rem;
	width: 17.5rem;
	z-index: 10;
	.is-mobile & {
		box-shadow: none;
		margin: 0 auto;
		padding: 1rem 0 0;
	}

	.align-right & {
		right: 0;
	}

	// Calendar header
	header {
		height: 2.5rem;
		display: flex;
		font-weight: 700;
		// All header content
		span {
			align-items: center;
			display: flex;
			flex: 1;
			justify-content: center;
			&:not(.disabled) {
				cursor: pointer;
			}
		}
		// Prev and Next buttons
		.prev,
		.next {
			flex: 0 0 2.5rem;
			font-size: 0;
			overflow: hidden;
			position: relative;
			&:after {
				border-style: solid;
				border-color: transparent transparent transparent currentColor;
				border-width: 0.38rem 0.62rem;
				content: "";
				height: 0;
				position: absolute;
				top: 50%;
				width: 0;
			}
		}
		.prev:after {
			border-color: transparent currentColor transparent transparent;
			transform: translate(-0.38rem, -50%);
		}
		.next:after {
			border-color: transparent transparent transparent currentColor;
			transform: translate(0.38rem, -50%);
		}
		// Calendar header hover
		.prev:not(.disabled):hover,
		.next:not(.disabled):hover,
		.up:not(.disabled):hover {
			background: $colour-teal--bright;
		}
	}

	// Calendar body
	div {
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
	}

	// Calendar body cells
	.cell {
		align-items: center;
		display: inline-flex;
		height: 2.5rem;
		justify-content: center;
		transition: color 150ms;
		width: calc(100% / 7);

		// Different cell-type widths
		&.month {
			width: calc(100% / 3);
		}
		&.year {
			width: calc(100% / 5);
		}

		// Everything clickable
		&:not(.blank):not(.disabled):not(.day-header) {
			cursor: pointer;
			position: relative;
			z-index: 2;
			// Circular indicator, initially with no opacity
			&:after {
				background: $colour-teal;
				border-radius: 1000px;
				content: "";
				height: 95%;
				left: 2.5%;
				opacity: 0;
				position: absolute;
				top: 0;
				transition: opacity 150ms;
				width: 95%;
				z-index: -1;
			}
			// Teal circle on hover
			&:hover {
				color: #fff;
				transition: color 0ms;
				&:after {
					opacity: 1;
					transition: opacity 0ms;
				}
			}
			// Teal circle when selected
			&.selected {
				color: #fff;
				&:after {
					opacity: 1;
				}
			}
		}

		// Calendar headers (days of the week)
		&.day-header {
			color: $colour-pencil--light;
			cursor: default;
		}
	}

	// Grey-out anything that's disabled
	.disabled {
		color: $colour-pencil--light;
	}
}
</style>
